import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { portalCommands } from 'src/app/utils/portal-path';
import { MajorsService } from 'src/app/services/majors.service';
import { Major } from './majors.models';

@Component({
  selector: 'app-majors',
  templateUrl: './majors.component.html',
  styleUrls: ['./majors.component.css']
})
export class MajorsComponent {
  items: Major[] = [];
  filteredItems: Major[] = [];
  selected: Major[] = [];
  searchTerm = '';
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [10, 20, 50];
  showDeleteModal = false;
  deleteModalBody = '';
  deleteModalParams: Record<string, unknown> = {};
  open = false;
  mode = 'add';
  selectedId = '';
  isError = false;
  errorMessage = '';
  form = new FormGroup({
    name: new FormControl('', [Validators.required])
  });

  constructor(
    private majorsService: MajorsService,
    private router: Router
  ) { }

  async ngOnInit() {
    await this.fetchItems();
  }

  async fetchItems() {
    this.items = await this.majorsService.list();
    this.applyFilter();
  }

  get paginatedItems(): Major[] {
    const start = this.pageIndex * this.pageSize;
    return this.filteredItems.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize));
  }

  get pageStart(): number {
    return this.filteredItems.length ? this.pageIndex * this.pageSize + 1 : 0;
  }

  get pageEnd(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.filteredItems.length);
  }

  applyFilter() {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredItems = term
      ? this.items.filter(item => item.name.toLowerCase().includes(term))
      : [...this.items];
    this.pageIndex = 0;
  }

  onSearchChange() {
    this.applyFilter();
  }

  onPageSizeChange(size: string) {
    this.pageSize = Number(size);
    this.pageIndex = 0;
  }

  previousPage() {
    if (this.pageIndex > 0) {
      this.pageIndex--;
    }
  }

  nextPage() {
    if (this.pageIndex < this.totalPages - 1) {
      this.pageIndex++;
    }
  }

  isSelected(item: Major): boolean {
    return this.selected.some(selected => selected.id === item.id);
  }

  toggleSelection(item: Major, event: Event) {
    event.stopPropagation();
    this.selected = this.isSelected(item)
      ? this.selected.filter(selected => selected.id !== item.id)
      : [...this.selected, item];
  }

  clearSelection() {
    this.selected = [];
  }

  openDetails(major: Major) {
    this.router.navigate(portalCommands(this.router, 'settings', 'majors', major.id));
  }

  onAdd() {
    this.mode = 'add';
    this.selectedId = '';
    this.form.reset();
    this.isError = false;
    this.open = true;
  }

  onEdit() {
    this.mode = 'edit';
    this.selectedId = this.selected[0].id;
    this.form.patchValue({ name: this.selected[0].name });
    this.isError = false;
    this.open = true;
  }

  onDeleteMany() {
    this.deleteModalBody = this.selected.length === 1 ? 'MAJORS.DELETE_ONE' : 'MAJORS.DELETE_MANY';
    this.deleteModalParams = this.selected.length === 1
      ? { name: this.selected[0].name }
      : { count: this.selected.length };
    this.showDeleteModal = true;
  }

  async onDeleteConfirm(confirmed: boolean) {
    if (confirmed) {
      for (const item of this.selected) {
        try {
          await this.majorsService.delete(item.id);
        } catch (error: any) {
          this.showError(error?.error?.errorMessages?.[0]?.message || 'MAJORS.DELETE_FAILED');
          this.showDeleteModal = false;
          await this.fetchItems();
          return;
        }
      }
      this.selected = [];
      await this.fetchItems();
    }
    this.showDeleteModal = false;
  }

  async submit() {
    if (this.form.invalid) {
      this.showError('Name is required');
      return;
    }

    try {
      const body = { name: this.form.value.name as string };
      if (this.mode === 'edit') {
        await this.majorsService.update(this.selectedId, body);
      } else {
        await this.majorsService.create(body);
      }
      this.open = false;
      this.selected = [];
      await this.fetchItems();
    } catch (error: any) {
      this.showError(error?.error?.errorMessages?.[0]?.message || 'MAJORS.SAVE_FAILED');
    }
  }

  closeModal() {
    this.open = false;
    this.isError = false;
  }

  showError(message: string) {
    this.isError = true;
    this.errorMessage = message;
  }
}
